import React, { useMemo, useState } from "react";
import "./styles/terms.css";

const TermsAndConditions = () => {
  const sections = useMemo(
    () => [
      {
        id: "welcome",
        num: 1,
        label: "WELCOME",
        blocks: [
          { h: "Terms and Conditions", p: "Welcome to Kupto." },
          {
            h: "",
            p: (
              <>
                These terms and conditions outline the rules and regulations for
                the use of Kupto&apos;s website, located at{" "}
                <a href="https://www.kupto2020.com" target="_blank" rel="noreferrer">
                  www.kupto2020.com
                </a>
                .
              </>
            ),
          },
          {
            h: "",
            p: `By accessing this website, placing an order, or using any of our services, you agree to be bound by these terms and conditions. If you do not agree with any part of these terms, please do not use Kupto’s website or services.`,
          },
        ],
      },
      {
        id: "definitions",
        num: 2,
        label: "DEFINITIONS",
        blocks: [
          { h: "1. Definitions", p: "" },
          {
            list: [
              { b: "Company", t: "Refers to Kupto." },
              {
                b: "Customer",
                t: "Refers to any individual, business, or organization purchasing products or services from Kupto.",
              },
              {
                b: "Website",
                t: (
                  <>
                    Refers to{" "}
                    <a
                      href="https://www.kupto2020.com"
                      target="_blank"
                      rel="noreferrer"
                    >
                      www.kupto2020.com
                    </a>
                    .
                  </>
                ),
              },
              {
                b: "Products",
                t: "Refers to all goods sold by Kupto, including stationery, printed materials, branded merchandise, office supplies, and other related items.",
              },
              {
                b: "Services",
                t: "Refers to printing, branding, customization, design support, delivery, and any other related services provided by Kupto.",
              },
            ],
          },
        ],
      },
      {
        id: "account",
        num: 3,
        label: "USER ACCOUNT",
        blocks: [
          { h: "2. User Account", p: "" },
          {
            list: [
              {
                t: "To access certain features of our website, you may be required to create an account.",
              },
              {
                t: "You are responsible for maintaining the confidentiality of your account details, including your password.",
              },
              {
                t: "You agree to provide accurate, current, and complete information when registering or placing an order.",
              },
              {
                t: "Kupto reserves the right to suspend or terminate accounts that contain false information or are used in a fraudulent or abusive manner.",
              },
            ],
          },
        ],
      },
      {
        id: "orders",
        num: 4,
        label: "ORDER PLACEMENT",
        blocks: [
          { h: "3. Order Placement", p: "" },
          {
            list: [
              {
                t: "By placing an order, you are offering to purchase a product or service subject to these terms and conditions.",
              },
              {
                t: "All orders are subject to availability, confirmation of pricing, and acceptance by Kupto.",
              },
              {
                t: "Kupto reserves the right to refuse, cancel, or limit any order at its sole discretion.",
              },
              {
                t: "For customized or branded orders, production only begins after final design approval and payment confirmation, unless otherwise agreed in writing.",
              },
            ],
          },
        ],
      },
      {
        id: "payment",
        num: 5,
        label: "PRICING & PAYMENT",
        blocks: [
          { h: "4. Pricing and Payment", p: "" },
          {
            list: [
              {
                t: "All prices listed on the website are in UGX (Ugandan Shillings) unless otherwise stated.",
              },
              {
                t: "Prices may vary depending on quantity, product specifications, customization, artwork requirements, and delivery location.",
              },
              {
                t: "Payment must be made at the time of purchase or before production begins, unless a separate payment arrangement has been approved by Kupto.",
              },
              {
                t: "Kupto reserves the right to update prices at any time without prior notice; however, confirmed orders will be charged at the agreed price at the time of confirmation.",
              },
            ],
          },
        ],
      },
      {
        id: "shipping",
        num: 6,
        label: "SHIPPING & DELIVERY",
        blocks: [
          { h: "5. Shipping and Delivery", p: "" },
          {
            list: [
              {
                t: "Kupto will deliver products to the address provided by the customer during checkout or order confirmation.",
              },
              {
                t: "Production and delivery timelines vary depending on the type of product, quantity ordered, customization requirements, and destination.",
              },
              {
                t: "While Kupto makes every effort to meet agreed timelines, we are not liable for delays caused by third-party couriers, supplier issues, weather conditions, technical failures, or other events beyond our control.",
              },
              {
                t: "Risk in the product passes to the customer upon delivery or collection.",
              },
            ],
          },
        ],
      },
      {
        id: "approval",
        num: 7,
        label: "DESIGN APPROVAL",
        blocks: [
          { h: "6. Design Approval", p: "" },
          {
            list: [
              {
                t: "Customers are responsible for carefully reviewing all proofs, previews, artwork, spellings, numbers, colors, and layouts before giving final approval.",
              },
              {
                t: "Once final approval is given, Kupto is not responsible for customer-approved errors, including spelling mistakes, design placement issues, or incorrect details.",
              },
              {
                t: "Any design changes requested after approval may attract additional charges and may affect production timelines.",
              },
            ],
          },
        ],
      },
      {
        id: "cancellation",
        num: 8,
        label: "CANCELLATION POLICY",
        blocks: [
          { h: "7. Cancellation Policy", p: "" },
          {
            list: [
              {
                t: "Orders may only be canceled before production, printing, customization, or processing has started.",
              },
              {
                t: "Once production has started, cancellations are not allowed for customized, branded, printed, or made-to-order items.",
              },
              {
                t: "If a cancellation is approved before production begins, any design, setup, or administrative costs already incurred may still be deducted from the refund.",
              },
            ],
          },
        ],
      },
      {
        id: "returns",
        num: 9,
        label: "RETURNS & REFUNDS",
        blocks: [
          { h: "8. Returns and Refunds", p: "" },
          {
            list: [
              {
                t: "Refunds or reprints are only available where the delivered product is defective, damaged due to our production process, or materially different from the approved design or confirmed order details.",
              },
              {
                t: "Complaints must be reported within 48 hours of delivery or collection, together with clear evidence such as photos, videos, or other relevant details.",
              },
              {
                t: "No refunds will be issued for customer-approved design errors, change of mind after production, or minor color variations that are reasonably expected in printing processes.",
              },
              {
                t: "Returned goods, where applicable, must be in their original condition unless the issue relates to a production defect.",
              },
              {
                t: "Where a claim is validated, Kupto may choose to provide a reprint, replacement, store credit, partial refund, or full refund depending on the nature of the issue.",
              },
              {
                t: "Approved refunds will be processed within a reasonable period after review and confirmation.",
              },
            ],
          },
        ],
      },
      {
        id: "ip",
        num: 10,
        label: "INTELLECTUAL PROPERTY",
        blocks: [
          { h: "9. Intellectual Property", p: "" },
          {
            list: [
              {
                t: "All content on the website, including text, graphics, logos, product images, designs, layouts, and branding, is the property of Kupto or its licensors unless otherwise stated.",
              },
              {
                t: "You may not copy, reproduce, republish, distribute, or commercially exploit any website content without prior written permission from Kupto.",
              },
              {
                t: "Customers are responsible for ensuring they have the legal right to use any logos, artwork, images, or text submitted for printing or branding.",
              },
            ],
          },
        ],
      },
      {
        id: "liability",
        num: 11,
        label: "LIMITATION OF LIABILITY",
        blocks: [
          { h: "10. Limitation of Liability", p: "" },
          {
            list: [
              {
                t: "Kupto shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our website, products, or services.",
              },
              {
                t: "Kupto’s total liability for any claim relating to a product or service shall not exceed the amount paid by the customer for that specific order.",
              },
              {
                t: "Kupto is not responsible for losses resulting from customer-provided errors, misuse of products, delayed approvals, or circumstances outside our reasonable control.",
              },
            ],
          },
        ],
      },
      {
        id: "law",
        num: 12,
        label: "GOVERNING LAW",
        blocks: [
          { h: "11. Governing Law", p: "" },
          {
            list: [
              {
                t: "These terms and conditions are governed by and interpreted in accordance with the laws of Uganda.",
              },
              {
                t: "Any disputes arising from these terms and conditions, the use of the website, or any purchase from Kupto shall be subject to the jurisdiction of the courts of Uganda.",
              },
            ],
          },
        ],
      },
      {
        id: "changes",
        num: 13,
        label: "CHANGES",
        blocks: [
          { h: "12. Changes to Terms and Conditions", p: "" },
          {
            list: [
              {
                t: "Kupto reserves the right to update, revise, or replace these terms and conditions at any time without prior notice.",
              },
              {
                t: "Any changes will become effective immediately upon being posted on the website.",
              },
              {
                t: "Your continued use of the website or services after changes are posted constitutes your acceptance of the revised terms and conditions.",
              },
            ],
          },
        ],
      },
      {
        id: "contact",
        num: 14,
        label: "CONTACT US",
        blocks: [
          { h: "13. Contact Us", p: "" },
          {
            list: [
              {
                t: (
                  <>
                    If you have any questions about these terms and conditions,
                    please contact us at{" "}
                    <a href="mailto:kupto2020@gmail.com">kupto2020@gmail.com</a>.
                  </>
                ),
              },
              {
                t: (
                  <>
                    Website:{" "}
                    <a
                      href="https://www.kupto2020.com"
                      target="_blank"
                      rel="noreferrer"
                    >
                      www.kupto2020.com
                    </a>
                  </>
                ),
              },
              {
                t: "Business Location: Kampala, Uganda.",
              },
            ],
          },
        ],
      },
    ],
    []
  );

  const [active, setActive] = useState("welcome");

  const goTo = (id) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderBlock = (b, idx) => {
    if (b.list) {
      return (
        <ul className="tc-ul" key={idx}>
          {b.list.map((li, i) => (
            <li key={i} className="tc-li">
              {li.b ? (
                <>
                  <strong>{li.b}:</strong> {li.t}
                </>
              ) : (
                li.t
              )}
            </li>
          ))}
        </ul>
      );
    }

    if (!b.h) {
      return (
        <p className="tc-p" key={idx}>
          {b.p}
        </p>
      );
    }

    return (
      <div key={idx}>
        <h2 className="tc-h2">{b.h}</h2>
        {b.p ? <p className="tc-p">{b.p}</p> : null}
      </div>
    );
  };

  return (
    <div className="tc-page">
      <div className="tc-shell">
        {/* LEFT NAV */}
        <aside className="tc-nav">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`tc-navItem ${active === s.id ? "active" : ""}`}
              onClick={() => goTo(s.id)}
            >
              <span className="tc-dot">{s.num}</span>
              <span className="tc-navText">{s.label}</span>
            </button>
          ))}
        </aside>

        {/* MAIN */}
        <main className="tc-main">
          <header className="tc-header">
            <h1 className="tc-title">Terms and Conditions</h1>
          </header>

          {sections.map((s) => (
            <section key={s.id} id={s.id} className="tc-section">
              <div className="tc-topLabel">{s.label}</div>
              {s.blocks?.map((b, idx) => renderBlock(b, idx))}

              {s.id === "welcome" && (
                <div className="tc-nextRow">
                  <span className="tc-readNext">Read next:</span>
                  <button
                    className="tc-pill"
                    type="button"
                    onClick={() => goTo("definitions")}
                  >
                    DEFINITIONS
                  </button>
                </div>
              )}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
};

export default TermsAndConditions;